import React, { Children, Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'

import { ErrorType } from '../../prop-types/error-prop-type'
import { ErrorPopover } from '../popover/error-popover'
import virtualListPropType from '../virtual-list/virtual-list-prop-type'
import SelectHeader from './select-header'
import SelectOptionList from './select-option-list'

import styles from './select.module.css'

const propTypes = {
    className: PropTypes.string,
    disabled: PropTypes.bool,
    error: ErrorType,
    header: PropTypes.element,
    headerClassName: PropTypes.string,
    list: PropTypes.element,
    multiple: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func,
    optionListClassName: PropTypes.string,
    optionListPlacement: PropTypes.oneOf(['top', 'bottom']),
    placeholder: PropTypes.string,
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    virtual: virtualListPropType
}

const defaultProps = {
    multiple: false,
    onChange() {}
}

class Select extends Component {
    constructor(props) {
        super(props)

        this.state = {
            dropdownOpen: false,
            selectedIndices: this.getSelectedIndicesFromChildrenProps(props)
        }

        this.onAutoCloseDropdown = this.onAutoCloseDropdown.bind(this)
        this.onDropdown = this.onDropdown.bind(this)
        this.onSelectChange = this.onSelectChange.bind(this)
    }

    UNSAFE_componentWillMount() {
        document.addEventListener('click', this.onAutoCloseDropdown, false)
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({ selectedIndices: this.getSelectedIndicesFromChildrenProps(nextProps) })
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.onAutoCloseDropdown, false)
    }

    render() {
        const { className, disabled, error } = this.props
        const { dropdownOpen } = this.state
        const classNameMerged = cx(
            'tag',
            styles['select'],
            {
                [styles['open']]: dropdownOpen,
                ['t3sel-select-disabled']: disabled,
                [styles['disabled']]: disabled,
                ['t3sel-select-error']: !disabled && error,
                [styles['error']]: !disabled && error,
                ['t3sel-select-open']: dropdownOpen
            },
            className
        )
        return (
            <div className={classNameMerged} data-error-anchor={!!error}>
                <ErrorPopover error={error} placement="top">
                    {this.renderHeader(dropdownOpen)}
                </ErrorPopover>
                {this.renderList(dropdownOpen)}
            </div>
        )
    }

    /**
     * Render the select header.
     *
     * Switches between the default and youa custom header element.
     *
     * @param {boolean} dropdownOpen - Whether the header should be rendered open or closed.
     * @returns {React.Element}
     */
    renderHeader(dropdownOpen) {
        const { children, header, headerClassName, optionListPlacement, placeholder } = this.props
        const onClick = this.onDropdown
        const selectedOptions = this.getSelectedChildren()
        const options = Children.toArray(children)

        return header ? (
            React.cloneElement(header, { dropdownOpen, headerClassName, onClick, options, selectedOptions })
        ) : (
            <SelectHeader
                {...{ headerClassName, onClick, optionListPlacement, options, placeholder, selectedOptions }}
            />
        )
    }

    /**
     * Render the option list.
     *
     * Switches between the default list and custom implementation.
     *
     * @param {boolean} dropdownOpen - Whether the list should be rendered open or closed.
     * @returns {React.Element}
     */
    renderList(dropdownOpen) {
        const { children, list, multiple, optionListClassName, optionListPlacement, virtual } = this.props
        const { selectedIndices } = this.state

        return (
            <SelectOptionList
                {...{ children, list, multiple, selectedIndices, virtual }}
                onChange={this.onSelectChange}
                open={dropdownOpen}
                className={optionListClassName}
                placement={optionListPlacement}
            />
        )
    }

    /**
     * Get the selectedIndices based off whatever values are found in this.props.values.
     *
     * @param {Object} props - Expecting children, value, multiple
     * @returns {number[]} The list of indices of ListOption elements that are selected.
     */
    getSelectedIndicesFromChildrenProps(props) {
        const { children, multiple, value } = props

        if (multiple && !Array.isArray(value)) {
            throw new Error('If Select#multiple is true, then value must be an Array.')
        }

        return Children.map(children, (child, key) => {
            const childValue = child && child.props && child.props.value
            const selected = multiple ? value.indexOf(childValue) !== -1 : childValue === value

            return { key, selected }
        })
            .filter(({ selected }) => selected)
            .map(({ key }) => key)
    }

    /**
     * Get the Option elements that are selected.
     *
     * @returns {React.Component[]} List of Option components that are listed in selectedIndices.
     */
    getSelectedChildren() {
        const { children } = this.props
        const { selectedIndices } = this.state

        return Children.toArray(children).filter((child, index) => selectedIndices.indexOf(index) !== -1)
    }

    /**
     * Get the selected value of this component.
     *
     * @returns {string|string[]} The value of the first Option or an Array
     *                           of all selected Option elements, if `this.props.multiple===true`.
     */
    getSelectedValue() {
        const { multiple } = this.props
        const values = this.getSelectedChildren().map(child => child.props.value)
        return multiple ? values : values.shift()
    }

    onSelectChange(selectedIndices) {
        const { multiple, onChange } = this.props
        const dropdownOpen = multiple

        this.setState(
            {
                dropdownOpen,
                selectedIndices
            },
            () => {
                onChange({
                    target: {
                        selectedIndices,
                        value: this.getSelectedValue()
                    }
                })
            }
        )
    }

    /**
     * Event handler - handle dropdown state.
     *
     * @param {Event} event - DOM event object.
     * @returns {void}
     */
    onDropdown(event) {
        event.preventDefault()
        let { dropdownOpen } = this.state
        const { disabled } = this.props

        if (!disabled) {
            dropdownOpen = !dropdownOpen
        }

        this.setState({ dropdownOpen })
    }

    /**
     * Event handler - handle autoclose.
     *
     * @param {Event} event - DOM event object.
     * @returns {void}
     */
    onAutoCloseDropdown(event) {
        const { dropdownOpen } = this.state
        if (dropdownOpen && !ReactDOM.findDOMNode(this).contains(event.target)) {
            this.setState({ dropdownOpen: false })
        }
    }
}

Select.propTypes = propTypes
Select.defaultProps = defaultProps

export default Select
